import { getJobNavLabels } from '@/helpers/jobNavLabels';
import { familyNodeId, groupId } from './helpers';

// --- Constants ---

export const JOB_ARCHITECTURE_ALERTS_FILTER_ID = 'job_architecture_alerts';

export const ARCHITECTURE_ALERT_FILTER_OPTIONS = [
  { id: 'empty_architecture', label: 'Empty Architecture' },
  { id: 'low_capacity', label: 'Low Capacity' },
  { id: 'unassigned_architecture', label: 'Unassigned Architecture' },
  { id: 'multiple_assignments', label: 'Multiple Assignments' },
];

export const ARCHITECTURE_ALERT_CODES_BY_CATEGORY = {
  empty_architecture: ['empty_job_profile', 'empty_job_family', 'empty_job_family_group'],
  low_capacity: ['low_capacity_job_profile', 'low_capacity_job_family', 'low_capacity_job_family_group'],
  unassigned_architecture: ['missing_job_family', 'missing_job_group'],
  multiple_assignments: ['multiple_job_families', 'multiple_job_groups'],
};

export const ARCHITECTURE_ALERT_PILL_LABELS = {
  empty_architecture: 'Empty',
  low_capacity: 'Low Capacity',
  unassigned_architecture: 'Unassigned Architecture',
  multiple_assignments: 'Multiple Assignments',
};

export const ARCHITECTURE_ALERT_CATEGORY_ORDER = [
  'empty_architecture',
  'low_capacity',
  'unassigned_architecture',
  'multiple_assignments',
];

const ARCHITECTURE_ENTITY_LABELS = {
  profile: { entity: 'job profile', assigned: 'employees', combineTarget: 'other job profiles' },
  family: { entity: 'job family', assigned: 'job profiles', combineTarget: 'other job families' },
  group: { entity: 'job family group', assigned: 'job families', combineTarget: 'other job family groups' },
};

export const ARCHITECTURE_ALERT_LEARN_MORE_ARTICLE_ID = '69e90c5f96a6df22f8735434';

export const ARCHITECTURE_ALERT_LEARN_MORE_URL = 'https://help.datapeople.io/article/349-job-architecture-alerts';

// --- Tree keys ---

// Returns true if the ulid belongs to a categorized node (not null, undefined, or blank).
export const isCategorized = (ulid) => ulid != null && String(ulid).trim() !== '';

// Returns the family's ulid if it has one, otherwise a fallback key for uncategorized families.
export const familyMapKey = (group, family) => (
  isCategorized(family?.ulid) ? family.ulid : familyNodeId(group, family)
);

// --- Hierarchy counts ---

// Counts how many categorized groups and families each profile belongs to across the whole tree.
export const buildProfileCounts = (treeData) => {
  const aggregate = new Map();

  (treeData?.groups || []).forEach((group) => {
    const groupIsCategorized = isCategorized(group?.ulid);

    (group?.families || []).forEach((family) => {
      const familyIsCategorized = isCategorized(family?.ulid);
      const familyName = family?.name?.trim() || 'Uncategorized';

      (family?.profiles || []).forEach((profile) => {
        const ulid = profile?.ulid;

        if (!isCategorized(ulid)) return;

        let entry = aggregate.get(ulid);

        if (!entry) {
          entry = {
            categorizedGroups: new Set(),
            categorizedFamilies: new Map(),
            employeeCount: Number(profile?.employee_count) || 0,
          };

          aggregate.set(ulid, entry);
        }

        if (groupIsCategorized) entry.categorizedGroups.add(group.ulid);
        if (familyIsCategorized) entry.categorizedFamilies.set(family.ulid, familyName);
      });
    });
  });

  const result = new Map();

  aggregate.forEach((entry, ulid) => {
    result.set(ulid, {
      categorizedGroupCount: entry.categorizedGroups.size,
      categorizedFamilyCount: entry.categorizedFamilies.size,
      categorizedFamilyNames: Array.from(entry.categorizedFamilies.values()),
      employeeCount: entry.employeeCount,
    });
  });

  return result;
};

// Counts how many categorized groups each family belongs to across the whole tree.
export const buildFamilyCounts = (treeData) => {
  const groupMaps = new Map();

  (treeData?.groups || []).forEach((group) => {
    const groupIsCategorized = isCategorized(group?.ulid);
    const groupName = group?.name?.trim() || 'Uncategorized';

    (group?.families || []).forEach((family) => {
      const key = familyMapKey(group, family);

      if (!groupMaps.has(key)) groupMaps.set(key, new Map());
      if (groupIsCategorized) groupMaps.get(key).set(group.ulid, groupName);
    });
  });

  const result = new Map();

  groupMaps.forEach((nameMap, key) => {
    result.set(key, {
      categorizedGroupCount: nameMap.size,
      categorizedGroupNames: Array.from(nameMap.values()),
    });
  });

  return result;
};

// --- Per-node alert rules ---

const isLowCapacity = (count) => count > 0 && count < 3;

// Returns the list of alert codes for a single profile based on its employee and hierarchy counts.
export const getProfileAlerts = (counts, { skipEmployeeAlerts = false } = {}) => {
  const alerts = [];
  const { categorizedFamilyCount, employeeCount } = counts;

  // Standalone has no employee data yet, so skip empty and low capacity employee alerts.
  if (!skipEmployeeAlerts) {
    if (employeeCount === 0) alerts.push('empty_job_profile');
    else if (isLowCapacity(employeeCount)) alerts.push('low_capacity_job_profile');
  }

  if (categorizedFamilyCount === 0) alerts.push('missing_job_family');
  if (categorizedFamilyCount > 1) alerts.push('multiple_job_families');

  return alerts;
};

// Returns the list of alert codes for a single job family based on its profiles and group count.
export const getFamilyAlerts = (family, counts) => {
  const alerts = [];
  const profileCount = family?.profiles?.length ?? 0;

  if (profileCount === 0) alerts.push('empty_job_family');
  else if (isLowCapacity(profileCount)) alerts.push('low_capacity_job_family');

  const { categorizedGroupCount } = counts;

  if (categorizedGroupCount === 0) alerts.push('missing_job_group');
  if (categorizedGroupCount > 1) alerts.push('multiple_job_groups');

  return alerts;
};

// Returns the list of alert codes for a single job family group based on its family count.
export const getGroupAlerts = (group) => {
  const alerts = [];
  const familyCount = group?.families?.length ?? 0;

  if (familyCount === 0) alerts.push('empty_job_family_group');
  else if (isLowCapacity(familyCount)) alerts.push('low_capacity_job_family_group');

  return alerts;
};

// --- Alert computation ---

// Converts the alert Maps to plain objects.
export const serializeArchitectureAlerts = (data) => ({
  byProfile: Object.fromEntries(data.byProfile),
  byFamily: Object.fromEntries(data.byFamily),
  byGroup: Object.fromEntries(data.byGroup),
});

// Runs all alert checks across the tree and returns alerts grouped by profile, family, and group.
export const computeArchitectureAlerts = (treeData, { skipEmployeeAlerts = false } = {}) => {
  const profileCounts = buildProfileCounts(treeData);
  const familyCounts = buildFamilyCounts(treeData);

  const byProfile = new Map();

  profileCounts.forEach((counts, ulid) => {
    byProfile.set(ulid, getProfileAlerts(counts, { skipEmployeeAlerts }));
  });

  const byFamily = new Map();

  const byGroup = new Map();

  (treeData?.groups || []).forEach((group) => {
    const gKey = groupId(group);

    byGroup.set(gKey, getGroupAlerts(group));

    (group?.families || []).forEach((family) => {
      const fKey = familyMapKey(group, family);
      const famCounts = familyCounts.get(fKey) ?? { categorizedGroupCount: 0 };

      byFamily.set(fKey, getFamilyAlerts(family, famCounts));
    });
  });

  return {
    byProfile,
    byFamily,
    byGroup,
    profileCounts,
    familyCounts,
  };
};

// --- Filtering ---

// Expands selected filter category IDs into concrete alert codes.
export const getMatchingAlertCodes = (selectedCategoryIds) => {
  const codes = new Set();

  (selectedCategoryIds || []).forEach((categoryId) => {
    (ARCHITECTURE_ALERT_CODES_BY_CATEGORY[categoryId] || []).forEach((code) => codes.add(code));
  });

  return codes;
};

// Maps a raw alert code back to its filter/display category.
export const getAlertCategoryForCode = (code) => {
  const matchingEntry = Object.entries(ARCHITECTURE_ALERT_CODES_BY_CATEGORY)
    .find(([, codesInCategory]) => codesInCategory.includes(code));

  if (!matchingEntry) {
    return null;
  }

  const categoryId = matchingEntry[0];

  return categoryId;
};

// Returns true if any of the node's alert codes match the filter set.
export const alertsMatchFilter = (nodeAlerts, matchingCodes) => (
  (nodeAlerts || []).some((code) => matchingCodes.has(code))
);

// Prune tree to groups, families, and profiles matching selected alert categories; parents are retained.
export const applyAlertFilters = (tree, selectedCategoryIds, { skipEmployeeAlerts = false } = {}) => {
  if (!selectedCategoryIds?.length || !tree?.groups) {
    return tree;
  }

  const matchingCodes = getMatchingAlertCodes(selectedCategoryIds);
  const { byProfile, byFamily, byGroup } = computeArchitectureAlerts(tree, { skipEmployeeAlerts });

  const profileMatches = (ulid) => {
    if (!isCategorized(ulid)) return false;

    return alertsMatchFilter(byProfile.get(ulid), matchingCodes);
  };

  const newGroups = [];

  tree.groups.forEach((group) => {
    const gKey = groupId(group);
    const groupMatches = alertsMatchFilter(byGroup.get(gKey), matchingCodes);
    const newFamilies = [];

    (group.families || []).forEach((family) => {
      const fKey = familyMapKey(group, family);
      const familyMatches = alertsMatchFilter(byFamily.get(fKey), matchingCodes);
      const matchingProfiles = (family.profiles || []).filter((profile) => profileMatches(profile?.ulid));

      if (matchingProfiles.length > 0) {
        newFamilies.push({ ...family, profiles: matchingProfiles });
      } else if (familyMatches) {
        newFamilies.push({ ...family, profiles: [] });
      }
    });

    if (newFamilies.length > 0) {
      newGroups.push({ ...group, families: newFamilies });
    } else if (groupMatches) {
      newGroups.push({ ...group, families: [] });
    }
  });

  return { ...tree, groups: newGroups };
};

// Sidebar alert checkboxes for the current tree slice.
export const deriveArchitectureAlertFilterOptions = (treeData, { skipEmployeeAlerts = false } = {}) => {
  if (!treeData?.groups?.length) {
    return [];
  }

  const { byProfile, byFamily, byGroup } = computeArchitectureAlerts(treeData, { skipEmployeeAlerts });
  const categories = new Set();

  [byProfile, byFamily, byGroup].forEach((map) => {
    map.forEach((codes) => {
      codes.forEach((code) => {
        const category = getAlertCategoryForCode(code);

        if (category) {
          categories.add(category);
        }
      });
    });
  });

  return ARCHITECTURE_ALERT_CATEGORY_ORDER
    .filter((id) => categories.has(id))
    .map((id) => ARCHITECTURE_ALERT_FILTER_OPTIONS.find((option) => option.id === id))
    .filter(Boolean);
};

// --- List view display ---

// Returns the pill label for a raw alert code.
export const getArchitectureAlertPillLabel = (code) => {
  const category = getAlertCategoryForCode(code);

  return category ? ARCHITECTURE_ALERT_PILL_LABELS[category] : '';
};

// Returns the expanded title for a raw alert code in the job profile preview sidebar.
export const getArchitectureAlertPreviewLabel = (code) => {
  const category = getAlertCategoryForCode(code);

  if (!category) return '';

  const filterOption = ARCHITECTURE_ALERT_FILTER_OPTIONS.find((option) => option.id === category);

  return filterOption?.label || ARCHITECTURE_ALERT_PILL_LABELS[category] || '';
};

const ARCHITECTURE_ALERT_PREVIEW_THEMES = {
  empty_architecture: {
    color: 'yellow',
    icon: 'mdi-minus-box-outline',
  },
  low_capacity: {
    color: 'yellow',
    icon: 'mdi-account-multiple-minus-outline',
  },
  unassigned_architecture: {
    color: 'yellow',
    icon: 'mdi-link-variant-off',
  },
  multiple_assignments: {
    color: 'yellow',
    icon: 'mdi-source-fork',
  },
};

const ARCHITECTURE_ALERT_PREVIEW_THEME_FALLBACK = {
  color: 'yellow',
  icon: 'mdi-alert-circle-outline',
};

// Returns PreviewAlertCard color and icon for a raw alert code.
export const getArchitectureAlertPreviewTheme = (code) => {
  const category = getAlertCategoryForCode(code);

  if (!category) {
    return ARCHITECTURE_ALERT_PREVIEW_THEME_FALLBACK;
  }

  return ARCHITECTURE_ALERT_PREVIEW_THEMES[category] || ARCHITECTURE_ALERT_PREVIEW_THEME_FALLBACK;
};

// Sorts alert codes by ARCHITECTURE_ALERT_CATEGORY_ORDER for pill stacking.
export const sortArchitectureAlertCodes = (codes) => [...(codes || [])].sort((codeA, codeB) => {
  const categoryA = getAlertCategoryForCode(codeA);
  const categoryB = getAlertCategoryForCode(codeB);
  const orderA = ARCHITECTURE_ALERT_CATEGORY_ORDER.indexOf(categoryA);
  const orderB = ARCHITECTURE_ALERT_CATEGORY_ORDER.indexOf(categoryB);

  return orderA - orderB;
});

// Builds hover tooltip copy for a pill based on category, node type, and assignment names.
export const getArchitectureAlertTooltip = ({
  code,
  nodeType,
  assignmentNames = [],
}) => {
  const labels = ARCHITECTURE_ENTITY_LABELS[nodeType];
  const category = getAlertCategoryForCode(code);
  const names = (assignmentNames || []).filter(Boolean).join(', ');
  const updateLocation = getJobNavLabels().architectureAlertUpdateLocation;

  if (!labels || !category) return '';

  switch (category) {
    case 'empty_architecture':
      return `This ${labels.entity} has no ${labels.assigned} assigned to it. If it is no longer in use, consider deactivating it ${updateLocation}.`;
    case 'low_capacity':
      return `This ${labels.entity} has fewer than 3 ${labels.assigned} assigned to it. If it makes sense to combine it with ${labels.combineTarget}, consider making that update ${updateLocation}.`;
    case 'unassigned_architecture':
      if (nodeType === 'profile') {
        return `This job profile is not assigned to a job family. If this is unintended, consider assigning a job family ${updateLocation}.`;
      }

      return `This job family is not assigned to a job family group. If this is unintended, consider assigning a job family group ${updateLocation}.`;
    case 'multiple_assignments':
      if (nodeType === 'profile') {
        return `This job profile is assigned to more than one job family: ${names}. If this is unintended, consider removing the unintended assignment ${updateLocation}.`;
      }

      return `This job family is assigned to more than one job family group: ${names}. If this is unintended, consider removing the unintended assignment ${updateLocation}.`;
    default:
      return '';
  }
};

// Supplies family names for multiple-assignment tooltips on profile rows.
export const getProfileAlertTooltipContext = (profileCounts, ulid) => ({
  familyNames: profileCounts.get(ulid)?.categorizedFamilyNames ?? [],
  groupNames: [],
});

// Supplies group names for multiple-assignment tooltips on family rows.
export const getFamilyAlertTooltipContext = (familyCounts, familyKey) => ({
  familyNames: [],
  groupNames: familyCounts.get(familyKey)?.categorizedGroupNames ?? [],
});

// Group rows have no assignment names for multiple-assignment tooltips.
export const getGroupAlertTooltipContext = () => ({
  familyNames: [],
  groupNames: [],
});
